import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";

import { RCSection, RCTabs, InvoiceUpload } from "../forms/RCListingForm.jsx";
import { PdfShower } from "../components";
import { IDoc } from "./FlowKit.jsx";
import { Button } from "../components/ui";
import { useEntitlement } from "../contexts";

/**
 * CONTRACTS — sits between INTERNAL DETAILS and PAYMENTS on the purpose-built
 * flows. Hosts the two doc tabs that used to live inside Internal Details
 * (Purchase Order / Upload Invoice → here "Invoices") plus the new AGREEMENTS
 * tab. All three tabs accept manual uploads.
 *
 * Persistence notes (current backend):
 *   - invoices    → listing_files.type='invoice', saves with the listing
 *                    exactly like the old Upload Invoice tab.
 *   - po_files /
 *     agreements  → their own listing_files types ('purchase_order' /
 *                    'agreement'), so they round-trip like invoices without
 *                    ever colliding with the Xero-generated PDFs shown above
 *                    them. On save an OMITTED key leaves the stored files
 *                    alone and an empty array clears them, matching invoices.
 *   - Sell-Request documents (Xero PO, purchasing/consignment agreements) are
 *     fetched read-only from the linked request; agreements generate lazily on
 *     click because their endpoints stamp *_agreement_sent_at on first
 *     generation.
 */
const ContractsSection = ({
  openSection,
  toggleSection,
  poUrl,                 // Xero PO of the selected Sell Request (or listing's own on edit)
  poEmptyNote,
  agreementSlots = [],   // [{ key, label, url, onLoad, loading, error }]
}) => {
  const [tab, setTab] = useState("po");
  const { values } = useFormikContext();

  const hasInvoices = (values?.invoices || []).length > 0;
  const hasManualPo = (values?.po_files || []).length > 0;
  const hasAgreements =
    (values?.agreements || []).length > 0 || agreementSlots.some((s) => s.url);

  // Generating a PO and generating an invoice are sold separately
  // (purchase_order_generation / invoice_generation). Both tabs were in the
  // Feature Matrix with nothing reading them, so switching either off did
  // nothing at all (Alex, 2026-10-26).
  //
  // A tab is kept when the feature is on OR when the store already HAS
  // documents of that kind — losing sight of a PO you have already issued
  // would be a worse failure than showing a tab you cannot add to.
  const hasPoFeature = useEntitlement('purchase_order_generation');
  const hasInvoiceFeature = useEntitlement('invoice_generation');

  const TABS = [
    ...(hasPoFeature || poUrl || hasManualPo
      ? [{ key: "po", label: "Purchase Order", dotOn: !!poUrl || hasManualPo }]
      : []),
    ...(hasInvoiceFeature || hasInvoices
      ? [{ key: "invoice", label: "Invoices", dotOn: hasInvoices }]
      : []),
    { key: "agreements", label: "Agreements",     dotOn: hasAgreements },
  ];

  // The default tab must exist, or the section opens on a pane that is not
  // in the list and renders nothing.
  useEffect(() => {
    if (!TABS.some((t) => t.key === tab)) setTab(TABS[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPoFeature, hasInvoiceFeature, tab]);

  return (
    <RCSection
      icon={<IDoc />}
      label="Contracts"
      isOpen={openSection === "contracts"}
      onToggle={() => toggleSection("contracts")}
    >
      <RCTabs tabs={TABS} active={tab} onChange={setTab} label="Contract documents" />

      {tab === "po" && (
        <div className="rc-lp__detail-docs rc-flow__contracts-pane">
          {poUrl ? (
            <div className="rc-lp__detail-pdf">
              <PdfShower fileId={poUrl} embedded label="Purchase Order" />
            </div>
          ) : (
            <div className="rc-lp__image-drop">
              <div className="rc-lp__image-drop-label">
                {poEmptyNote ||
                  "No purchase order yet — it appears here once fetched from the linked Sell Request."}
              </div>
            </div>
          )}
          <div className="rc-flow__upload-extra">
            <div className="rc-field__label">Upload manually</div>
            <InvoiceUpload name="po_files" />
          </div>
        </div>
      )}

      {tab === "invoice" && (
        <div className="rc-lp__detail-invoice rc-flow__contracts-pane">
          <InvoiceUpload name="invoices" />
        </div>
      )}

      {tab === "agreements" && (
        <div className="rc-flow__contracts-pane">
          {agreementSlots.map((slot) => (
            <div key={slot.key} className="rc-flow__agreement-slot">
              <div className="rc-field__label">{slot.label}</div>
              {slot.url ? (
                <div className="rc-lp__detail-pdf">
                  <PdfShower fileId={slot.url} embedded label={slot.label} />
                </div>
              ) : slot.onLoad ? (
                <Button
                  variant="secondary"
                  size="sm"
                  data-btn="agreement-open"
                  onClick={slot.onLoad}
                  disabled={slot.loading}
                >
                  {slot.loading ? "Fetching…" : `Fetch ${slot.label} from Sell Request`}
                </Button>
              ) : (
                <div className="rc-flow__hint">
                  Available once the listing is linked to a Sell Request.
                </div>
              )}
              {slot.error && <div className="rc-flow__ai rc-flow__ai--unavailable">{slot.error}</div>}
            </div>
          ))}
          <div className="rc-flow__upload-extra">
            <div className="rc-field__label">Upload manually</div>
            <InvoiceUpload name="agreements" />
          </div>
        </div>
      )}
    </RCSection>
  );
};

export default ContractsSection;
